import React, { useState, useEffect, useRef } from 'react';
import { MapPin, X, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

const SwissPostAutocomplete = ({
  value = '',
  onChange,
  onPlaceSelect,
  placeholder = 'PLZ oder Ort eingeben...',
  disabled = false,
  className = '',
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchSwissPost = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // OpenPLZ API for Swiss addresses (free, no key needed)
      const response = await fetch(
        `https://openplzapi.org/ch/Localities?name=${encodeURIComponent(query)}&limit=10`
      );
      const data = await response.json();
      
      const results = data.map((record) => ({
        postal_code: record.postalCode,
        city: record.name,
        canton: record.canton?.shortName || record.canton?.name || '',
        municipality: record.municipality?.name || record.name,
      }));
      
      // Remove duplicates based on postal_code + city
      const unique = results.filter((item, index, self) =>
        index === self.findIndex((t) => t.postal_code === item.postal_code && t.city === item.city)
      );
      
      setSuggestions(unique);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Swiss address API error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange?.(val);

    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchSwissPost(val);
    }, 300);
  };

  const handleSelect = (item) => {
    const formatted = `${item.postal_code} ${item.city}`;
    setInputValue(formatted);
    onChange?.(formatted);
    onPlaceSelect?.({
      postal_code: item.postal_code,
      city: item.city,
      canton: item.canton,
      municipality: item.municipality,
      formatted_address: formatted,
    });
    setShowSuggestions(false);
  };

  const handleClear = () => {
    setInputValue('');
    onChange?.('');
    onPlaceSelect?.(null);
    setSuggestions([]);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10"
          autoComplete="off"
          data-testid="swiss-post-autocomplete"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {!isLoading && inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((item, index) => (
            <button
              key={`${item.postal_code}-${item.city}-${index}`}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
              data-testid={`suggestion-${index}`}
            >
              <div className="font-medium text-gray-900">
                {item.postal_code} {item.city}
              </div>
              <div className="text-sm text-gray-500">
                Kanton {item.canton} • {item.municipality}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SwissPostAutocomplete;
